import type { Editor, TLStore } from "tldraw";
import type { WriteMode, CollabMode } from "./store/useSpaceStore";
import { SLIDE_EMBED_TYPE } from "./shapes/SlideEmbedShapeUtil";
import { SHARED_LAYER } from "./constants";

export type { CollabMode };

// Flag set while spaces-diff-filter is processing a sync-originated remove so
// that registerBeforeDeleteHandler can allow it through (the handler has no
// source parameter and cannot distinguish user vs sync otherwise).
let _isInSyncRemove = false;
export function isInSyncRemove(): boolean {
  return _isInSyncRemove;
}

/**
 * Applies write guard to the TLDraw editor based on writeMode + collabMode.
 *
 * | writeMode | collabMode | behaviour                                        |
 * |-----------|------------|--------------------------------------------------|
 * | read      | any        | all local shape writes blocked (view-only)       |
 * | edit      | local      | all shapes local-only (nothing reaches server)   |
 * | edit      | shared     | no interception, everything syncs                |
 *
 * Returns a cleanup fn that removes any monkey-patches applied.
 *
 * Idempotent: if called multiple times on the same editor, always cleans up
 * the previous patch before applying a new one. This prevents double-patching
 * when both onWhiteboardMount and useEffect([writeMode, collabMode]) fire.
 */
export function applyDiffFilter(
  editor: Editor,
  writeMode: WriteMode,
  collabMode: CollabMode = "local",
  personalStore?: TLStore | null,
  userId?: string,
): (() => void) | void {
  const store = editor.store as any;

  // Idempotency guard: clean up any existing patch before applying a new one.
  if (store.__diffFilterCleanup) {
    store.__diffFilterCleanup();
    store.__diffFilterCleanup = undefined;
  }

  if (writeMode === "read") {
    const cleanup = enableReadMode(editor);
    store.__diffFilterCleanup = cleanup;
    return cleanup;
  }
  if (collabMode === "local") {
    if (personalStore && userId) {
      const cleanup = enablePersonalRoutingMode(editor, personalStore, userId);
      store.__diffFilterCleanup = cleanup;
      return cleanup;
    }
    // Legacy: block all shapes from reaching shared room (client-only)
    const cleanup = enablePersonalMode(editor);
    store.__diffFilterCleanup = cleanup;
    return cleanup;
  }
  // edit + shared: full access, no interception needed
  editor.updateInstanceState({ isReadonly: false });
}

// ---------------------------------------------------------------------------
// Read mode: block ALL local shape writes — only server-originated changes pass
// ---------------------------------------------------------------------------

function enableReadMode(editor: Editor): () => void {
  editor.updateInstanceState({ isReadonly: true });
  const store = editor.store as any;
  const hadOwnPut = Object.prototype.hasOwnProperty.call(store, "put");
  const hadOwnRemove = Object.prototype.hasOwnProperty.call(store, "remove");
  const origPut = store.put as (...args: unknown[]) => unknown;
  const origRemove = store.remove as (...args: unknown[]) => unknown;

  store.put = function (records: unknown[], phaseOverride?: unknown) {
    const isMerging = store.isMergingRemoteChanges === true;
    const shapes = (records as any[]).filter((r) => r?.typeName === "shape");
    const nonShapes = (records as any[]).filter((r) => r?.typeName !== "shape");

    // Non-shape records (camera, instance state) always pass through.
    if (nonShapes.length > 0) origPut.call(store, nonShapes, phaseOverride);

    // Shape changes: only allow server-originated (sync) updates.
    if (shapes.length > 0 && isMerging) {
      origPut.call(store, shapes, phaseOverride);
    }
  };

  store.remove = function (ids: unknown[]) {
    const isMerging = store.isMergingRemoteChanges === true;
    const shapeIds = (ids as string[]).filter(
      (id) => (store.get(id) as any)?.typeName === "shape",
    );
    const nonShapeIds = (ids as string[]).filter(
      (id) => (store.get(id) as any)?.typeName !== "shape",
    );

    if (nonShapeIds.length > 0) origRemove.call(store, nonShapeIds);
    if (shapeIds.length > 0 && isMerging) origRemove.call(store, shapeIds);
  };

  return makeCleanup(store, hadOwnPut, hadOwnRemove, origPut, origRemove);
}

// ---------------------------------------------------------------------------
// Personal mode: ALL records stay local-only
// ---------------------------------------------------------------------------

function enablePersonalMode(editor: Editor): () => void {
  editor.updateInstanceState({ isReadonly: false });

  const store = editor.store as any;
  const hadOwnPut = Object.prototype.hasOwnProperty.call(store, "put");
  const hadOwnRemove = Object.prototype.hasOwnProperty.call(store, "remove");
  const origPut = store.put as (...args: unknown[]) => unknown;
  const origRemove = store.remove as (...args: unknown[]) => unknown;

  let isInMerge = false;

  store.put = function (records: unknown[], phaseOverride?: unknown) {
    if (isInMerge) return origPut.call(store, records, phaseOverride);

    const shapes = (records as any[]).filter((r) => r?.typeName === "shape");
    const nonShapes = (records as any[]).filter((r) => r?.typeName !== "shape");

    const isMerging = store.isMergingRemoteChanges === true;

    // Non-shape records (instance, camera, cursor position) go through directly.
    // They are high-frequency user-state updates that must never be wrapped in
    // mergeRemoteChanges, calling it during a sync atomic op (e.g. push_result
    // from gateway) throws "cannot merge while in atomic operation". Moving the
    // cursor while the AI is writing shapes is exactly when this collision occurs.
    if (nonShapes.length > 0) origPut.call(store, nonShapes, phaseOverride);

    if (shapes.length === 0) return;

    // Embed shapes (teacher slides) are immovable for students. Since this patch
    // wraps all puts in mergeRemoteChanges, registerBeforeChangeHandler always sees
    // source="remote" and cannot distinguish user drags from real sync updates.
    // Gate here instead: only allow embed updates that arrive from the sync server
    // (store.isMergingRemoteChanges=true means we're inside @tldraw/sync's own
    // mergeRemoteChanges, not our own isInMerge guard).
    const embedShapes = shapes.filter((r) => r?.type === SLIDE_EMBED_TYPE);
    const nonEmbedShapes = shapes.filter((r) => r?.type !== SLIDE_EMBED_TYPE);
    if (embedShapes.length > 0) {
      if (isMerging) origPut.call(store, embedShapes, phaseOverride);
    }
    if (nonEmbedShapes.length === 0) return;

    // Stamp isPersonal on shapes before origPut, registerBeforeCreateHandler
    // won't run because source will be "remote" inside mergeRemoteChanges.
    const stamped = nonEmbedShapes.map((r) => {
      if (r?.meta?.isPersonal) return r; // already stamped
      // Teacher shapes are never personal, they live on the shared canvas only.
      // NOTE: if we add a feature where teachers can annotate directly on a student's
      // personal canvas, this guard will need to be revisited (teacher shapes directed
      // at a specific student would need isPersonal = true on that student's client).
      if (r?.meta?.role === "teacher") return r;
      return { ...r, meta: { ...(r.meta ?? {}), isPersonal: true } };
    });
    isInMerge = true;
    try {
      store.mergeRemoteChanges(() =>
        origPut.call(store, stamped, phaseOverride),
      );
    } finally {
      isInMerge = false;
    }
  };

  store.remove = function (ids: unknown[]) {
    if (isInMerge) return origRemove.call(store, ids);

    const shapeIds = (ids as string[]).filter(
      (id) => (store.get(id) as any)?.typeName === "shape",
    );
    const nonShapeIds = (ids as string[]).filter(
      (id) => (store.get(id) as any)?.typeName !== "shape",
    );

    if (nonShapeIds.length > 0) origRemove.call(store, nonShapeIds);

    if (shapeIds.length === 0) return;

    // If this remove originates from the sync server, pass the flag through so
    // registerBeforeDeleteHandler can allow it (the handler has no source param).
    const fromSync = store.isMergingRemoteChanges === true;
    isInMerge = true;
    _isInSyncRemove = fromSync;
    try {
      store.mergeRemoteChanges(() => origRemove.call(store, shapeIds));
    } finally {
      isInMerge = false;
      _isInSyncRemove = false;
    }
  };

  return makeCleanup(store, hadOwnPut, hadOwnRemove, origPut, origRemove);
}

// ---------------------------------------------------------------------------
// Personal routing mode: route shape puts to personalStore by meta.layer
// ---------------------------------------------------------------------------

/**
 * Input:  editor whose store is the shared sync store
 *         personalStore — the personal TLSync store for this user
 *         userId — meta.layer value that identifies personal shapes
 * Mutates: patches editor.store.put to intercept shapes with meta.layer === userId
 *
 * Personal shapes (meta.layer === userId) are:
 *   • Kept in editor.store wrapped in mergeRemoteChanges → shared useSync won't broadcast them
 *   • Also written to personalStore via a direct put (no mergeRemoteChanges) → personal useSync
 *     sends them to the DO personal room
 *
 * Shared shapes (meta.layer !== userId) go through origPut normally → shared useSync broadcasts.
 * Server-originated puts (isMergingRemoteChanges === true) are never intercepted.
 *
 * The reverse listener (personalStore → editor.store) in useStudentSpaceCanvases handles
 * DO updates arriving for this user's personal room.
 */
function enablePersonalRoutingMode(
  editor: Editor,
  personalStore: TLStore,
  userId: string,
): () => void {
  const store = editor.store as any;
  const hadOwnPut = Object.prototype.hasOwnProperty.call(store, "put");
  const hadOwnRemove = Object.prototype.hasOwnProperty.call(store, "remove");
  const origPut = store.put as (...args: unknown[]) => unknown;
  const origRemove = store.remove as (...args: unknown[]) => unknown;

  // Prevents the reverse listener (personalStore → editor.store) from re-entering
  // this patch while we're already routing a local put.
  let isRoutingPersonal = false;

  // A binding inherits "personal" classification from the shapes it connects:
  // if either endpoint is a personal-layer shape, the binding routes with it.
  // Bindings (e.g. arrow attachments) have no meta.layer of their own — without
  // this, an AI-drawn arrow's shape lands in personalStore but its binding goes
  // to shared, and the arrow's attachment is lost on reconnect / for the
  // teacher viewing Student Work.
  const isPersonalBinding = (rec: any): boolean => {
    if (rec?.typeName !== "binding") return false;
    const fromShape: any = store.get(rec.fromId);
    const toShape: any = store.get(rec.toId);
    return fromShape?.meta?.layer === userId || toShape?.meta?.layer === userId;
  };

  store.put = function (records: unknown[], phaseOverride?: unknown) {
    // Server-originated OR reverse-listener re-entry: pass through directly
    if (store.isMergingRemoteChanges === true || isRoutingPersonal) {
      return origPut.call(store, records, phaseOverride);
    }

    const shapes = (records as any[]).filter((r) => r?.typeName === "shape");
    const bindings = (records as any[]).filter(
      (r) => r?.typeName === "binding",
    );
    const otherRecords = (records as any[]).filter(
      (r) => r?.typeName !== "shape" && r?.typeName !== "binding",
    );

    if (otherRecords.length > 0)
      origPut.call(store, otherRecords, phaseOverride);

    // Pre-stamp meta.layer = userId on shapes that don't have one yet. The
    // personal-shape branch wraps origPut in mergeRemoteChanges (source="remote"),
    // and registerBeforeCreateHandler only stamps on source="user" — so without
    // this pre-stamp the first create diff would arrive with no meta.layer and
    // be misclassified as shared.
    //
    // Shapes with meta.layer === "shared" are shared-canvas content (teacher
    // drawings). A user-action put on them is a student trying to move/edit
    // shared content — drop it silently so the shape snaps back. This is
    // consistent with beforeChangeHandler returning prev for role=teacher, but
    // works even when source="remote" bypasses that handler.
    const taggedShapes = shapes
      .filter((r: any) => r?.meta?.layer !== SHARED_LAYER)
      .map((r: any) => {
        if (r?.meta?.layer) return r; // already stamped (update diffs)
        return { ...r, meta: { ...(r.meta ?? {}), layer: userId } };
      });

    const sharedShapes = taggedShapes.filter(
      (r: any) => r?.meta?.layer !== userId,
    );
    const personalShapes = taggedShapes.filter(
      (r: any) => r?.meta?.layer === userId,
    );

    // Pre-classify bindings using shapes in THIS batch AND what's already in store.
    // Batch-first covers the case where shape + binding arrive in the same call
    // (the shape isn't in the store yet at classification time, but its layer is
    // already known from taggedShapes above).
    const personalShapeIds = new Set(personalShapes.map((r: any) => r.id));
    const isPersonalBindingInBatch = (rec: any): boolean => {
      if (rec?.typeName !== "binding") return false;
      return (
        personalShapeIds.has(rec.fromId) ||
        personalShapeIds.has(rec.toId) ||
        isPersonalBinding(rec)
      );
    };
    const personalBindings = bindings.filter(isPersonalBindingInBatch);
    const sharedBindings = bindings.filter((b) => !isPersonalBindingInBatch(b));

    if (sharedShapes.length > 0)
      origPut.call(store, sharedShapes, phaseOverride);
    if (sharedBindings.length > 0)
      origPut.call(store, sharedBindings, phaseOverride);

    const allPersonal = [...personalShapes, ...personalBindings];
    if (allPersonal.length > 0) {
      // Wrap in mergeRemoteChanges so shared useSync sees source="remote" and does
      // not broadcast personal records. mergeRemoteChanges throws when called during
      // a tldraw atomic op (e.g. ArrowBindingUtil.onAfterCreate → editor.updateShape).
      // Defer to a microtask in that case — the atomic op completes synchronously
      // before the next microtask runs, so the deferred commit succeeds without throwing.
      const commit = () => {
        store.mergeRemoteChanges(() =>
          origPut.call(store, allPersonal, phaseOverride),
        );
        isRoutingPersonal = true;
        try {
          personalStore.put(allPersonal);
        } finally {
          isRoutingPersonal = false;
        }
      };
      try {
        commit();
      } catch {
        queueMicrotask(commit);
      }
    }
  };

  store.remove = function (ids: unknown[]) {
    // Server-originated OR reverse-listener re-entry: pass through directly.
    // For server-originated removes, set _isInSyncRemove so registerBeforeDeleteHandler
    // (which has no source parameter) can distinguish them from user-driven removes.
    if (store.isMergingRemoteChanges === true || isRoutingPersonal) {
      const fromSync = store.isMergingRemoteChanges === true;
      if (fromSync) _isInSyncRemove = true;
      try {
        return origRemove.call(store, ids);
      } finally {
        if (fromSync) _isInSyncRemove = false;
      }
    }

    const idList = ids as string[];
    const shapeIds: string[] = [];
    const bindingIds: string[] = [];
    const otherIds: string[] = [];
    for (const id of idList) {
      const tn = (store.get(id) as any)?.typeName;
      if (tn === "shape") shapeIds.push(id);
      else if (tn === "binding") bindingIds.push(id);
      else otherIds.push(id);
    }

    if (otherIds.length > 0) origRemove.call(store, otherIds);
    if (shapeIds.length === 0 && bindingIds.length === 0) return;

    const sharedShapeRemoveIds: string[] = [];
    const personalShapeRemoveIds: string[] = [];
    for (const id of shapeIds) {
      const rec: any = store.get(id);
      if (rec?.meta?.layer === userId) personalShapeRemoveIds.push(id);
      else sharedShapeRemoveIds.push(id);
    }

    // Bindings inherit classification from their endpoints (same rule as put).
    const sharedBindingRemoveIds: string[] = [];
    const personalBindingRemoveIds: string[] = [];
    for (const id of bindingIds) {
      if (isPersonalBinding(store.get(id))) personalBindingRemoveIds.push(id);
      else sharedBindingRemoveIds.push(id);
    }

    if (sharedShapeRemoveIds.length > 0)
      origRemove.call(store, sharedShapeRemoveIds);
    if (sharedBindingRemoveIds.length > 0)
      origRemove.call(store, sharedBindingRemoveIds);

    const personalRemoveIds = [
      ...personalShapeRemoveIds,
      ...personalBindingRemoveIds,
    ];
    if (personalRemoveIds.length > 0) {
      // Mirror locally as "remote" so shared useSync does not broadcast a
      // remove for records it never knew about, then remove from personalStore
      // so personal useSync sends the delete to the DO personal room.
      store.mergeRemoteChanges(() => origRemove.call(store, personalRemoveIds));

      isRoutingPersonal = true;
      try {
        personalStore.remove(personalRemoveIds as any);
      } finally {
        isRoutingPersonal = false;
      }
    }
  };

  return makeCleanup(store, hadOwnPut, hadOwnRemove, origPut, origRemove);
}

// ---------------------------------------------------------------------------
// Shared cleanup helper
// ---------------------------------------------------------------------------

function makeCleanup(
  store: any,
  hadOwnPut: boolean,
  hadOwnRemove: boolean,
  origPut: (...args: unknown[]) => unknown,
  origRemove: (...args: unknown[]) => unknown,
): () => void {
  return () => {
    store.__diffFilterCleanup = undefined;
    if (hadOwnPut) store.put = origPut;
    else delete store.put;
    if (hadOwnRemove) store.remove = origRemove;
    else delete store.remove;
  };
}
